import type { SluggedId } from "Models";
import { leaguesV2Api } from "../leaguesV2Api";
import type {
  Clubhouse_PostLeagueV2Round,
  Clubhouse_PatchLeagueV2Round,
  Clubhouse_LeagueRoundV2Dto,
} from "../../types/leagues";
import type {
  Clubhouse_LeagueOperatorRoundDetailDto,
  Clubhouse_LeagueRoundFinalizationStatusDto,
  Clubhouse_LeagueRoundFinalizeResultDto,
  Clubhouse_PostLeagueOperatorScorecardReviewAction,
} from "../../types/leagueRound";
import type {
  Clubhouse_LeagueV2RoundScorecardsDto,
  Clubhouse_LeagueV2OrphanRecoveryResponseDto,
  Clubhouse_LeagueV2ScorecardSearchPageDto,
  Clubhouse_LeagueV2ScorecardSearchResultDto,
  Clubhouse_LeagueV2ScorecardWriteRequest,
} from "../../types/scorecards";
import type {
  Clubhouse_LeagueV2SubstituteSearchResponseDto,
  Clubhouse_LeagueV2RoundParticipantSubstituteAssignmentDto,
  Clubhouse_PatchLeagueV2RoundParticipant,
} from "../../types/substitutes";

type CreateLeagueRoundArgs = {
  leagueId: string;
  facilityId: string;
  body: Clubhouse_PostLeagueV2Round;
};

type UpdateLeagueRoundArgs = {
  leagueId: string;
  facilityId: string;
  roundId: string;
  body: Clubhouse_PatchLeagueV2Round;
};

type DeleteLeagueRoundArgs = {
  leagueId: string;
  facilityId: string;
  // String to match the round row's id — no numeric coercion at the call site.
  roundId: string;
};

type FinalizeLeagueRoundArgs = {
  leagueId: string;
  facilityId: string;
  roundId: string;
};

type GetLeagueRoundArgs = {
  leagueId: string;
  roundId: string;
};

type GetLeagueRoundParticipantReviewArgs = {
  leagueId: string;
  roundId: string;
  participantId: SluggedId;
};

type ApplyScorecardReviewActionArgs = {
  leagueId: string;
  roundId: string;
  participantId: SluggedId;
  body: Clubhouse_PostLeagueOperatorScorecardReviewAction;
};

type GetLeagueRoundScorecardsArgs = {
  facilityId: string;
  leagueId: string;
  roundId: string;
};

/** Server-side search mode for the scorecard window. */
export type LeagueV2ScorecardSearchMode = "Suggested" | "Expanded";

type SearchRoundScorecardsArgs = {
  facilityId: string;
  leagueId: string;
  roundId: string;
  mode: LeagueV2ScorecardSearchMode;
  /** Required for suggested search (identity-first). */
  participantId?: string;
  /** Expanded: partial match on simulator username (optional). */
  name?: string;
  /** Internal/support: direct savegame lookup (not used in operator UI). */
  savegameId?: number;
  /** Expanded: inclusive UTC start of played-at range. */
  playedFromUtc?: string;
  /** Expanded: inclusive UTC end of played-at range. */
  playedToUtc?: string;
  /** Expanded pagination: pass nextCursor from prior response. */
  cursor?: string;
  /** Busts RTK Query cache when the operator runs a new search (ignored by the API). */
  searchGeneration?: number;
};

/** Coerce V2 search payloads into the shape RTK/UI expect (camelCase + page wrapper). */
export function normalizeScorecardSearchPage(
  response: unknown,
): Clubhouse_LeagueV2ScorecardSearchPageDto {
  if (Array.isArray(response)) {
    return {
      results: response as Clubhouse_LeagueV2ScorecardSearchResultDto[],
    };
  }

  if (!response || typeof response !== "object") {
    return { results: [] };
  }

  const record = response as Record<string, unknown>;
  const rawResults = record.results ?? record.Results;
  const results = Array.isArray(rawResults)
    ? (rawResults as Clubhouse_LeagueV2ScorecardSearchResultDto[])
    : [];

  const nextCursor = record.nextCursor ?? record.NextCursor;
  const hasMore = record.hasMore ?? record.HasMore;

  return {
    results,
    nextCursor:
      typeof nextCursor === "string"
        ? nextCursor
        : nextCursor == null
          ? null
          : String(nextCursor),
    hasMore: Boolean(hasMore),
  };
}

type RecoverOrphanScorecardsArgs = {
  leagueId: string;
  roundId: string;
};

type SaveParticipantScorecardArgs = {
  facilityId: string;
  leagueId: string;
  roundId: string;
  participantId: SluggedId;
  body: Clubhouse_LeagueV2ScorecardWriteRequest;
};

type GetLeagueRoundsArgs = {
  facilityId: string;
  leagueId: string;
};

type SearchRoundSubstitutesArgs = {
  facilityId: string;
  leagueId: string;
  roundId: string;
  query: string;
};

type PatchRoundParticipantSubstituteArgs = {
  facilityId: string;
  leagueId: string;
  roundId: string;
  roundParticipantId: SluggedId;
  /** lg_members.id to assign, or null to clear back to the rostered player. */
  substituteMemberId: number | null;
};

/**
 * A scorecard write moves the round's own surfaces (participant table, review
 * sheet, Scorecards tab — all League-tagged) and can resolve an adjudication
 * issue. Results and Standings are recompute-only snapshots the backend
 * refreshes when it rescores, so they are invalidated too: a corrected card
 * changes the round's leaderboard and the league table behind it.
 */
const scorecardWriteTags = (
  _result: unknown,
  _error: unknown,
  { leagueId, roundId }: { leagueId: string; roundId: string },
) => [
  { type: "League" as const, id: leagueId },
  "AdjudicationQueue" as const,
  "Dashboard" as const,
  { type: "Standings" as const, id: leagueId },
  { type: "RoundResults" as const, id: roundId },
];

/**
 * The scorecard-search URL.
 *
 * Built by hand rather than with URLSearchParams, which encodes a space as
 * `+`. That form is only defined for a form body; in a query string `+` is a
 * literal and ASP.NET Core does not decode it back to a space, so a search for
 * "Bill Nye" would be matched against "Bill+Nye" and find nobody.
 * Percent-encoding means the same thing in both places.
 */
export function roundScorecardSearchUrl({
  facilityId,
  leagueId,
  roundId,
  mode,
  participantId,
  name,
  savegameId,
  playedFromUtc,
  playedToUtc,
  cursor,
  searchGeneration,
}: SearchRoundScorecardsArgs): string {
  const query = [
    `mode=${mode}`,
    participantId != null && participantId !== ""
      ? `participantId=${encodeURIComponent(participantId)}`
      : null,
    name != null && name !== ""
      ? `name=${encodeURIComponent(name)}`
      : null,
    savegameId != null && savegameId > 0 ? `savegameId=${savegameId}` : null,
    playedFromUtc != null && playedFromUtc !== ""
      ? `playedFromUtc=${encodeURIComponent(playedFromUtc)}`
      : null,
    playedToUtc != null && playedToUtc !== ""
      ? `playedToUtc=${encodeURIComponent(playedToUtc)}`
      : null,
    cursor != null && cursor !== ""
      ? `cursor=${encodeURIComponent(cursor)}`
      : null,
    searchGeneration != null && searchGeneration > 0
      ? `searchGeneration=${searchGeneration}`
      : null,
  ]
    .filter((part): part is string => part != null)
    .join("&");
  return `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/scorecards/search?${query}`;
}

export const roundsEndpoints = leaguesV2Api.injectEndpoints({
  endpoints: (build) => ({
    getLeagueRound: build.query<
      Clubhouse_LeagueOperatorRoundDetailDto,
      GetLeagueRoundArgs
    >({
      query: ({ leagueId, roundId }) =>
        `/api/leagues/${leagueId}/rounds/${roundId}`,
      providesTags: (_, __, { leagueId }) => [{ type: "League", id: leagueId }],
    }),
    // Same round-detail endpoint with `?participantId=` — the server then
    // populates `focusedParticipantReview` (linked + candidate savegames) for
    // that one roster row. Its own cache key (participantId) keeps the main
    // round table stable while the scorecard-review sheet loads independently.
    getLeagueRoundParticipantReview: build.query<
      Clubhouse_LeagueOperatorRoundDetailDto,
      GetLeagueRoundParticipantReviewArgs
    >({
      query: ({ leagueId, roundId, participantId }) =>
        `/api/leagues/${leagueId}/rounds/${roundId}?participantId=${participantId}`,
      // Scope the review tag to the round — a participant id repeats across a
      // league's rounds (and could collide across leagues), so a participant-only
      // id would let a future targeted invalidation refetch unrelated reviews.
      providesTags: (_, __, { leagueId, roundId, participantId }) => [
        { type: "League", id: leagueId },
        {
          type: "ScorecardReview",
          id: `${leagueId}:${roundId}:${participantId}`,
        },
      ],
    }),
    // Link / Reject / Mark-Played-No-Scorecard / Mark-No-Show for one
    // participant. Returns the refreshed round detail focused on that row.
    // Operator-scoped `/api/leagues/...` (not facility league-v2): test APIM
    // already exposes this POST; facility search uses league-v2 GET only.
    applyScorecardReviewAction: build.mutation<
      Clubhouse_LeagueOperatorRoundDetailDto,
      ApplyScorecardReviewActionArgs
    >({
      query: ({ leagueId, roundId, participantId, body }) => ({
        url: `/api/leagues/${leagueId}/rounds/${roundId}/participants/${participantId}/scorecard-review-actions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { leagueId, roundId, participantId }) => [
        { type: "League", id: leagueId },
        {
          type: "ScorecardReview",
          id: `${leagueId}:${roundId}:${participantId}`,
        },
        "AdjudicationQueue",
        "Dashboard",
      ],
    }),
    // Full hole-by-hole customer scorecards for every participant in a round
    // (unlinked rows come back with an empty hole list). Note the `league-v2`
    // URL segment — distinct from the `leagues` segment the review/recovery
    // actions use.
    getLeagueRoundScorecards: build.query<
      Clubhouse_LeagueV2RoundScorecardsDto,
      GetLeagueRoundScorecardsArgs
    >({
      query: ({ facilityId, leagueId, roundId }) =>
        `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/scorecards`,
      providesTags: (_, __, { leagueId }) => [{ type: "League", id: leagueId }],
    }),
    // Facility savegames this round could link, ranked by the server, at most
    // 20, each with the hole grid the window previews before linking. Note the
    // `league-v2` URL segment — the review and link actions sit on `leagues`.
    //
    // Not tagged League: the result is a view of the facility's savegames, not
    // of the round, and a link elsewhere in the round does not change which
    // savegames exist. Refetching every search on every round write would throw
    // away the operator's results mid-decision.
    searchRoundScorecards: build.query<
      Clubhouse_LeagueV2ScorecardSearchPageDto,
      SearchRoundScorecardsArgs
    >({
      query: roundScorecardSearchUrl,
      transformResponse: normalizeScorecardSearchPage,
    }),
    // On-demand orphan recovery: match SDK savegames to unlinked participants,
    // auto-link strong matches, and open adjudication for the rest.
    recoverOrphanScorecards: build.mutation<
      Clubhouse_LeagueV2OrphanRecoveryResponseDto,
      RecoverOrphanScorecardsArgs
    >({
      query: ({ leagueId, roundId }) => ({
        url: `/api/leagues/${leagueId}/rounds/${roundId}/orphan-scorecard-recovery`,
        method: "POST",
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
        "AdjudicationQueue",
        "Dashboard",
      ],
    }),
    // Operator correction of a linked card, and operator entry of a card for a
    // round that never saved. Two endpoints rather than one because the server
    // treats them differently: the edit stamps `scorecardSource: Edited` and
    // keeps the linked savegame, the manual entry creates a card with no
    // savegame behind it and stamps `Manual`. Both share one body and both
    // answer 409 when the participant's `version` has moved on. The edit is
    // allowed after finalization.
    saveParticipantScorecardEdit: build.mutation<
      Clubhouse_LeagueV2RoundScorecardsDto,
      SaveParticipantScorecardArgs
    >({
      query: ({ facilityId, leagueId, roundId, participantId, body }) => ({
        url: `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/participants/${participantId}/scorecard`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: scorecardWriteTags,
    }),
    createParticipantScorecard: build.mutation<
      Clubhouse_LeagueV2RoundScorecardsDto,
      SaveParticipantScorecardArgs
    >({
      query: ({ facilityId, leagueId, roundId, participantId, body }) => ({
        url: `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/participants/${participantId}/scorecard/manual`,
        method: "POST",
        body,
      }),
      invalidatesTags: scorecardWriteTags,
    }),
    // Eligible substitute candidates for a round (name/phone/email query).
    // Tagged by leagueId; the assign/clear mutation invalidates the same League
    // tag, so results (and their isAlreadyAssignedThisRound flags) refetch.
    searchRoundSubstitutes: build.query<
      Clubhouse_LeagueV2SubstituteSearchResponseDto,
      SearchRoundSubstitutesArgs
    >({
      query: ({ facilityId, leagueId, roundId, query }) =>
        `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/substitutes/search?query=${encodeURIComponent(query)}`,
      providesTags: (_, __, { leagueId }) => [{ type: "League", id: leagueId }],
    }),
    // Assign (substituteMemberId) or clear (null) the substitute on one slot.
    patchRoundParticipantSubstitute: build.mutation<
      Clubhouse_LeagueV2RoundParticipantSubstituteAssignmentDto,
      PatchRoundParticipantSubstituteArgs
    >({
      query: ({
        facilityId,
        leagueId,
        roundId,
        roundParticipantId,
        substituteMemberId,
      }) => ({
        url: `/api/facility/${facilityId}/league-v2/${leagueId}/rounds/${roundId}/participants/${roundParticipantId}`,
        method: "PATCH",
        body: {
          substituteMemberId,
        } satisfies Clubhouse_PatchLeagueV2RoundParticipant,
      }),
      // League refreshes the live surfaces (participant table, scorecard review,
      // scorecards tab — all League-tagged). Results/Standings/ScoringDetails are
      // recompute-only snapshots the backend does NOT touch on a substitute
      // change (only on an explicit scoring action), so they're intentionally
      // not invalidated here — doing so would refetch an unchanged snapshot.
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
        "Dashboard",
      ],
    }),
    getLeagueRounds: build.query<
      Clubhouse_LeagueRoundV2Dto[],
      GetLeagueRoundsArgs
    >({
      query: ({ facilityId, leagueId }) =>
        `/api/facility/${facilityId}/leagues/${leagueId}/rounds`,
      providesTags: (_, __, { leagueId }) => [{ type: "League", id: leagueId }],
    }),
    createLeagueRound: build.mutation<{ id?: number }, CreateLeagueRoundArgs>({
      query: ({ facilityId, leagueId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/rounds`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
      ],
    }),
    updateLeagueRound: build.mutation<void, UpdateLeagueRoundArgs>({
      query: ({ facilityId, leagueId, roundId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/rounds/${roundId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
      ],
    }),
    deleteLeagueRound: build.mutation<void, DeleteLeagueRoundArgs>({
      query: ({ facilityId, leagueId, roundId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/rounds/${roundId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        { type: "League", id: leagueId },
        "Dashboard",
      ],
    }),
    finalizeLeagueRound: build.mutation<
      Clubhouse_LeagueRoundFinalizeResultDto,
      FinalizeLeagueRoundArgs
    >({
      query: ({ facilityId, leagueId, roundId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/rounds/${roundId}/finalize`,
        method: "POST",
      }),
      invalidatesTags: (_, __, { leagueId, roundId }) => [
        { type: "League", id: leagueId },
        "Dashboard",
        // Backend auto-scores on finalize — refresh the standings read.
        { type: "Standings", id: leagueId },
        // roundId is a string slug; RoundResults tags are provided with the same
        // string id, so no coercion — the invalidation matches directly.
        { type: "RoundResults", id: roundId },
      ],
    }),
    getFinalizationStatus: build.query<
      Clubhouse_LeagueRoundFinalizationStatusDto,
      FinalizeLeagueRoundArgs
    >({
      query: ({ facilityId, leagueId, roundId }) =>
        `/api/facility/${facilityId}/leagues/${leagueId}/rounds/${roundId}/finalization-status`,
      providesTags: (_, __, { leagueId }) => [{ type: "League", id: leagueId }],
    }),
  }),
});

export const {
  useCreateLeagueRoundMutation,
  useUpdateLeagueRoundMutation,
  useDeleteLeagueRoundMutation,
  useFinalizeLeagueRoundMutation,
  useGetLeagueRoundQuery,
  useGetLeagueRoundParticipantReviewQuery,
  useApplyScorecardReviewActionMutation,
  useGetLeagueRoundScorecardsQuery,
  useSearchRoundScorecardsQuery,
  useRecoverOrphanScorecardsMutation,
  useSaveParticipantScorecardEditMutation,
  useCreateParticipantScorecardMutation,
  useSearchRoundSubstitutesQuery,
  usePatchRoundParticipantSubstituteMutation,
  useGetLeagueRoundsQuery,
  useGetFinalizationStatusQuery,
} = roundsEndpoints;
